import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Calendar, Clock, User, ArrowRight, ChevronDown } from "lucide-react";
import { GoldButton, NavyButton } from "@/components/ui/button-variants";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LanguageSelector } from "@/components/ui/language-selector";
import { ScrollToTop } from "@/components/ui/scroll-to-top";
import { useLanguage } from "@/contexts/LanguageContext";
import { loadPostsByLang } from "@/blog/loader";
import type { LoadedPost } from "@/blog/types";
import { setMetaTags } from "@/seo/seo";
import NewsletterForm from "@/components/newsletter/NewsletterForm";
import heroBackground from "@/assets/hero-background.jpg";

const PER_PAGE = 9;

export default function Blog() {
  const { t, language } = useLanguage();
  const lang: "en" | "fr" = language === "fr" ? "fr" : "en";

  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState<LoadedPost[]>([]);

  const currentTag = searchParams.get('tag') || (lang === 'fr' ? 'Tous' : 'All');
  const currentPage = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);

  // Load posts
  useEffect(() => { 
    console.log('Loading posts for language:', lang);
    loadPostsByLang(lang).then(loaded => {
      console.log('Posts loaded:', loaded.length, loaded);
      setPosts(loaded);
    }).catch(err => {
      console.error('Error loading posts:', err);
    });
  }, [lang]);

  // SEO landing + hreflang alternates
  useEffect(() => {
    const base = window.location.origin;
    setMetaTags({
      title: lang === 'fr' ? 'Blog AcquizGroup' : 'AcquizGroup Blog',
      description: lang === 'fr'
        ? "Conseils d'experts, analyses de marché et guides pratiques pour réussir vos transactions d'acquisition et de transmission d'entreprise."
        : "Expert insights, market analysis, and practical guides for successful SMB acquisitions and transfers.",
      url: `${base}/blog`,
      locale: lang === 'fr' ? 'fr_FR' : 'en_US',
      type: 'website'
    });

    // hreflang
    const alts = [
      { hreflang: 'fr', href: `${base}/blog?lang=fr` },
      { hreflang: 'en', href: `${base}/blog?lang=en` },
      { hreflang: 'x-default', href: `${base}/blog` },
    ];
    alts.forEach(({ hreflang, href }) => {
      let el = document.head.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`) as HTMLLinkElement | null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', 'alternate');
        el.setAttribute('hreflang', hreflang);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
    });
  }, [lang]);

  // Categories (tags)
  const categories = useMemo(() => {
    const set = new Set<string>();
    posts.forEach(p => (p.frontmatter.tags || []).forEach(tag => set.add(tag)));
    return [lang === "fr" ? "Tous" : "All", ...Array.from(set)];
  }, [posts, lang]);

  // Filter & paginate
  const filtered = useMemo(() => {
    if (currentTag === 'Tous' || currentTag === 'All') return posts;
    return posts.filter(p => p.frontmatter.tags?.includes(currentTag));
  }, [posts, currentTag]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const page = Math.min(currentPage, totalPages);
  const start = (page - 1) * PER_PAGE;
  const pageItems = filtered.slice(start, start + PER_PAGE);

  const featured = posts.slice(0, 3);

  function setTag(tag: string) {
    const next = new URLSearchParams(searchParams);
    if (tag === 'Tous' || tag === 'All') next.delete('tag'); 
    else next.set('tag', tag);
    next.set('page', '1');
    setSearchParams(next, { replace: true });
  }

  function setPage(nextPage: number) {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(nextPage));
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="bg-navy/95 backdrop-blur-sm border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="hover:opacity-80 transition-opacity">
                <img src="/lovable-uploads/f2ee99e5-4bf1-45fe-b66f-7da448cc9f9a.png" alt="AcquizGroup Logo" className="h-12 w-auto" />
              </Link>
            </div>
            <div className="hidden md:flex space-x-8 items-center">
              <DropdownMenu>
                <DropdownMenuTrigger className="text-white/90 hover:text-white transition-colors flex items-center gap-1">
                  {t('nav.services')}
                  <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white border border-gray-200 shadow-lg">
                  <DropdownMenuItem asChild><Link to="/seller" className="cursor-pointer">{t('nav.sellers')}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/landing/buyers" className="cursor-pointer">{t('nav.buyers')}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/partners" className="cursor-pointer">{t('nav.consultants')}</Link></DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger className="text-white/90 hover:text-white transition-colors flex items-center gap-1">
                  {lang === 'fr' ? 'Ressources' : 'Resources'}
                  <ChevronDown className="h-4 w-4" />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-white border border-gray-200 shadow-lg">
                  <DropdownMenuItem asChild><Link to="/listings" className="cursor-pointer">{lang === 'fr' ? 'À vendre' : 'For Sale'}</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/blog" className="cursor-pointer">Blog</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/resources/e-learning" className="cursor-pointer">E-learning</Link></DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Link to="/about" className="text-white/90 hover:text-white transition-colors">{t('nav.about')}</Link>
              <LanguageSelector />
              <Link to="/login" className="px-4 py-2 bg-white text-navy font-medium rounded-md hover:bg-white/90 transition-colors">
                Login
              </Link>
              <Link to="/contact"><GoldButton size="sm">Contact</GoldButton></Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="bg-navy relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden flex items-center justify-center">
          <img src={heroBackground} alt="" className="min-w-full min-h-full object-cover blur-[2px] opacity-45" />
        </div>
        <div className="absolute inset-0 bg-navy/60"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              {lang === 'fr' ? 'Blog AcquizGroup' : 'AcquizGroup Blog'}
            </h1>
            <p className="text-xl text-white/90 mb-12 max-w-3xl mx-auto">
              {lang === 'fr' 
                ? "Conseils d'experts, analyses de marché et guides pratiques pour réussir vos transactions d'acquisition et de transmission d'entreprise." 
                : "Expert insights, market analysis, and practical guides for successful SMB acquisitions and transfers."}
            </p>
          </div>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="py-8 bg-background border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-4 justify-center">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setTag(c)}
                className={`px-6 py-2 rounded-full border transition-colors ${
                  c === currentTag 
                    ? "bg-gold text-white border-gold" 
                    : "bg-white text-foreground border-border hover:border-gold hover:text-gold"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              {lang === 'fr' ? 'Articles en vedette' : 'Featured Articles'}
            </h2>
            <p className="text-xl text-muted-foreground">
              {lang === 'fr' ? 'Nos derniers conseils et analyses.' : 'Our latest guidance and analysis.'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {featured.map((post) => (
              <Card key={post.frontmatter.slug} className="hover:shadow-lg transition-shadow flex flex-col h-full">
                {post.frontmatter.hero ? (
                  <img src={post.frontmatter.hero} alt="" loading="lazy" className="h-48 w-full object-cover rounded-t-lg" />
                ) : (
                  <div className="h-48 bg-gradient-to-br from-navy/20 to-gold/20 rounded-t-lg" />
                )}
                <CardHeader className="flex-1">
                  <div className="flex justify-between items-start mb-4">
                    <Badge variant="secondary">
                      {post.frontmatter.tags?.[0] || (lang==='fr'?'Général':'General')}
                    </Badge>
                    <div className="flex items-center text-muted-foreground text-sm">
                      <Clock className="h-4 w-4 mr-1" />
                      {estimateReadTime(post.content)}
                    </div>
                  </div>
                  <CardTitle className="text-xl mb-2">{post.frontmatter.title}</CardTitle>
                  <CardDescription className="text-base mb-4">{post.frontmatter.excerpt}</CardDescription>
                  <div className="flex items-center justify-between text-sm text-muted-foreground mt-auto">
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-1" />
                      AcquizGroup
                    </div>
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      {new Date(post.frontmatter.date).toLocaleDateString()}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <GoldButton asChild className="w-full">
                    <Link to={`/blog/${post.frontmatter.slug}`}>
                      {lang==='fr'?"Lire l'article":"Read article"} 
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </GoldButton>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* All Articles (paginated) */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              {lang==='fr'?"Tous nos articles":"All posts"}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {pageItems.map((post) => (
              <Card key={post.frontmatter.slug} className="hover:shadow-lg transition-shadow flex flex-col h-full">
                {post.frontmatter.hero ? (
                  <img src={post.frontmatter.hero} alt="" loading="lazy" className="h-32 w-full object-cover rounded-t-lg" />
                ) : (
                  <div className="h-32 bg-gradient-to-br from-navy/10 to-gold/10 rounded-t-lg" />
                )}
                <CardHeader className="pb-2 flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <Badge variant="outline" className="text-xs">
                      {post.frontmatter.tags?.[0] || (lang==='fr'?'Général':'General')}
                    </Badge>
                    <div className="flex items-center text-muted-foreground text-xs">
                      <Clock className="h-3 w-3 mr-1" />
                      {estimateReadTime(post.content)}
                    </div>
                  </div>
                  <CardTitle className="text-lg leading-tight">{post.frontmatter.title}</CardTitle>
                  <CardDescription className="text-sm mb-4 line-clamp-3 mt-2">
                    {post.frontmatter.excerpt}
                  </CardDescription>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-auto">
                    <div className="flex items-center">
                      <User className="h-3 w-3 mr-1" />
                      AcquizGroup
                    </div>
                    <div className="flex items-center">
                      <Calendar className="h-3 w-3 mr-1" />
                      {new Date(post.frontmatter.date).toLocaleDateString()}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 mt-4">
                  <NavyButton asChild size="sm" className="w-full">
                    <Link to={`/blog/${post.frontmatter.slug}`}>
                      {lang==='fr'?"Lire l'article":"Read article"}
                    </Link>
                  </NavyButton>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              <button 
                className="px-3 py-2 rounded border disabled:opacity-50" 
                onClick={() => setPage(Math.max(1, page - 1))} 
                disabled={page <= 1}
              >
                {lang==='fr'?"Précédent":"Prev"}
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                <button 
                  key={n} 
                  onClick={() => setPage(n)} 
                  className={`px-3 py-2 rounded border ${
                    n === page 
                      ? 'bg-gold text-white border-gold' 
                      : 'bg-white hover:border-gold hover:text-gold'
                  }`}
                >
                  {n}
                </button>
              ))}
              <button 
                className="px-3 py-2 rounded border disabled:opacity-50" 
                onClick={() => setPage(Math.min(totalPages, page + 1))} 
                disabled={page >= totalPages}
              >
                {lang==='fr'?"Suivant":"Next"}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-navy/5 to-gold/5 rounded-2xl p-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">
              {lang==='fr'?"Restez informé des dernières tendances":"Stay in the loop"}
            </h2>
            <p className="text-xl text-muted-foreground mb-8">
              {lang==='fr'?"Recevez nos meilleurs conseils par email.":"Get our best insights by email."}
            </p>
            <div className="max-w-md mx-auto">
              <NewsletterForm 
                lang={lang} 
                placeholder={lang==='fr'?"Votre adresse email":"Your email"} 
                cta={lang==='fr'?"S'abonner":"Subscribe"} 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-navy text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="footer-logo-wrap mb-8">
            <img src="/noBgColor.png" alt="AcquizGroup" className="ag-logo-footer" />
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4 text-gold">
                {lang==='fr'?"À propos d'AcquizGroup":"About AcquizGroup"}
              </h3>
              <p className="text-white/80 text-sm">
                {lang==='fr'?"Nous relions l'ambition à l'action.":"We connect ambition to action."}
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">
                {lang==='fr'?"Services":"Services"}
              </h3>
              <ul className="space-y-2 text-sm text-white/80">
                <li>
                  <a href="#" className="hover:text-gold transition-colors">
                    {lang==='fr'?"Vendre une entreprise":"Sell a business"}
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-gold transition-colors">
                    {lang==='fr'?"Acheter une entreprise":"Buy a business"}
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-gold transition-colors">
                    {lang==='fr'?"Devenir Partenaire":"Become a partner"}
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">
                {lang==='fr'?"Ressources":"Resources"}
              </h3>
              <ul className="space-y-2 text-sm text-white/80">
                <li>
                  <a href="#" className="hover:text-gold transition-colors">Deal Monitor</a>
                </li>
                <li>
                  <Link to="/blog" className="hover:text-gold transition-colors">Blog</Link>
                </li>
                <li>
                  <a href="#" className="hover:text-gold transition-colors">FAQ</a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact</h3>
              <p className="text-sm text-white/80 mb-4">
                {lang==='fr'?"Parlez‑nous de votre projet.":"Tell us about your project."}
              </p>
              <GoldButton size="sm" onClick={() => window.location.href = '/contact'}>
                {lang==='fr'?"Contacter l'équipe":"Contact The Team"}
              </GoldButton>
            </div>
          </div>
          <div className="border-t border-white/20 mt-12 pt-8 text-center text-sm text-white/60">
            <p>
              © {new Date().getFullYear()} AcquizGroup. {lang==='fr'?"Tous droits réservés.":"All rights reserved."}
            </p>
          </div>
        </div>
      </footer>
      <ScrollToTop />
    </div>
  );
}

function estimateReadTime(markdown: string) {
  const words = markdown.trim().split(/\s+/).length;
  const min = Math.max(1, Math.round(words / 200));
  return `${min} min`;
}
